import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTriageMessage } from "@/features/messages/hooks/use-messages";
import {
  URGENCY_ORDER,
  type ClientMessage,
  type MessageUrgency,
} from "@/features/messages/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { CaretDownIcon, PencilSimpleIcon } from "@phosphor-icons/react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useCategoryLabels } from "./categories";

/** `datetime-local` speaks the browser's timezone, and epoch milliseconds do not. */
const toLocalInput = (timestamp: number): string => {
  const date = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Runs a triage change and says so, or says why not. */
const useTriage = (message: ClientMessage) => {
  const { t } = useTranslation("messages");
  const triage = useTriageMessage();
  const change = async (
    next: { urgency?: MessageUrgency; category?: string; due_at?: number },
    onDone?: () => void,
  ) => {
    try {
      await triage.mutateAsync({ id: message.id, ...next });
      toast.success(t("triage.done"));
      onDone?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("handle.failed"));
    }
  };
  return { change, busy: triage.isPending };
};

/** A badge that opens a menu of choices when the request can still be changed. */
function BadgeMenu({
  editable,
  badge,
  value,
  options,
  onChange,
  busy,
}: {
  editable: boolean;
  badge: (caret: ReactNode) => ReactNode;
  value: string;
  options: { value: string; label: ReactNode }[];
  onChange: (value: string) => void;
  busy: boolean;
}) {
  const caret = editable ? (
    <CaretDownIcon className="size-3" weight="bold" />
  ) : null;
  if (!editable) return <>{badge(null)}</>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={busy}>
        <button type="button" className="rounded-full">
          {badge(caret)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The category of a request, and the way to file it elsewhere. */
export function CategoryBadge({
  message,
  editable,
}: {
  message: ClientMessage;
  editable: boolean;
}) {
  const { categories, labelOf, colorOf } = useCategoryLabels();
  const { change, busy } = useTriage(message);
  // A request keeps a category the tenant removed; it is listed so it can be left as is.
  const keys = categories.map((c) => c.key);
  const current = message.category || "other";
  if (!keys.includes(current)) keys.push(current);

  return (
    <BadgeMenu
      editable={editable}
      busy={busy}
      value={current}
      options={keys.map((key) => ({
        value: key,
        label: (
          <span className="flex items-center gap-2">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: colorOf(key) }}
            />
            {labelOf(key)}
          </span>
        ),
      }))}
      onChange={(category) =>
        category !== current && change({ category })
      }
      badge={(caret) => (
        <Badge color={colorOf(current)}>
          {labelOf(current)}
          {caret}
        </Badge>
      )}
    />
  );
}

/** The urgency of a request, and the way to change it. */
export function UrgencyBadge({
  message,
  editable,
}: {
  message: ClientMessage;
  editable: boolean;
}) {
  const { t } = useTranslation("messages");
  const { change, busy } = useTriage(message);
  const urgency = message.urgency || "normal";
  const pending = message.status === "pending";

  return (
    <BadgeMenu
      editable={editable}
      busy={busy}
      value={urgency}
      options={URGENCY_ORDER.map((value) => ({
        value,
        label: t(`urgency.${value}`),
      }))}
      onChange={(next) =>
        next !== urgency && change({ urgency: next as MessageUrgency })
      }
      badge={(caret) => (
        <Badge
          variant="outline"
          className={cn(
            pending && urgency === "high"
              ? "border-red-500/40 text-red-600"
              : "text-muted-foreground",
          )}
        >
          {t(`urgency.${urgency}`)}
          {caret}
        </Badge>
      )}
    />
  );
}

/** A deadline, and a small pencil to set another one. */
export function DeadlineEditor({
  message,
  editable,
  children,
}: {
  message: ClientMessage;
  editable: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation("messages");
  const { format } = useDateFormat();
  const { change, busy } = useTriage(message);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  const at = value ? new Date(value).getTime() : 0;
  const valid = at > Date.now();

  if (!editable) return <>{children}</>;

  return (
    <span className="flex items-center gap-1.5">
      {children}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (next) setValue(toLocalInput(message.due_at || Date.now()));
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-6 text-muted-foreground"
            aria-label={t("triage.editDeadline")}
          >
            <PencilSimpleIcon className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="space-y-3">
          <div className="space-y-1.5">
            <label
              htmlFor={`deadline-${message.id}`}
              className="text-sm font-medium"
            >
              {t("triage.deadlineLabel")}
            </label>
            <Input
              id={`deadline-${message.id}`}
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {valid
                ? format(at, "Pp")
                : t("triage.inThePast")}
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              disabled={!valid || busy}
              onClick={() => change({ due_at: at }, () => setOpen(false))}
            >
              {t("triage.save")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}
